import { User, Menu, Shield, LogOut, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/useAuthStore';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { NotificationBell } from './NotificationBell';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export function Header() {
  const { t } = useTranslation('common');
  const {
    user,
    isAuthenticated,
    logout
  } = useAuthStore();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  useEffect(() => {
    if (user?.id) {
      checkAdminStatus();
    }
  }, [user?.id]);
  
  const checkAdminStatus = async () => {
    if (!user?.id) return;
    
    const { data } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'admin')
      .maybeSingle();
      
    setIsAdmin(!!data);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    
    try {
      // Timeout de 5 segundos para garantir que logout sempre completa
      const timeoutPromise = new Promise<void>((_, reject) => 
        setTimeout(() => reject(new Error('Logout timeout')), 5000)
      );
      
      await Promise.race([
        logout(),
        timeoutPromise
      ]);
      
      toast({
        title: t('header.logoutSuccess') || 'Desconectado com sucesso',
      });
      
      // Redirecionar para landing page
      setTimeout(() => {
        window.location.href = '/';
      }, 300);
      
    } catch (error) {
      console.error('Erro no logout:', error);
      
      // Fallback: forçar limpeza local e reload
      toast({
        title: t('header.logoutError') || 'Saindo...',
        description: 'Redirecionando para a página inicial',
      });
      
      // Forçar limpeza e reload como último recurso
      localStorage.clear();
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
      
    } finally {
      setIsLoggingOut(false);
    }
  };
  
  return <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4">
        <Link to={isAuthenticated ? "/home" : "/"} className="flex items-center space-x-2">
          <img 
            src="/logo/logo-icon.png" 
            alt="Trilha Da Disciplina" 
            className="h-10 w-10 sm:hidden"
          />
          <img 
            src="/logo/logo-horizontal.png" 
            alt="Trilha Da Disciplina" 
            className="hidden sm:block h-10"
          />
        </Link>

        {isAuthenticated && user ? <div className="flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                {user.avatar_url ? (
                  <Avatar key={user.avatar_url} className="h-8 w-8">
                    <AvatarImage src={user.avatar_url} />
                    <AvatarFallback>{user.nome?.[0] || 'U'}</AvatarFallback>
                  </Avatar>
                ) : (
                  <User className="h-5 w-5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user.nome}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/perfil">{t('header.profile')}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/home">{t('header.dashboard')}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/descobrir">{t('header.discoverGroups')}</Link>
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="text-primary font-medium">
                      <Shield className="h-4 w-4 mr-2" />
                      {t('admin.adminPanel')}
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="md:py-1.5 py-3 text-red-600 cursor-pointer"
                disabled={isLoggingOut}
                onSelect={(e) => {
                  e.preventDefault();
                  handleLogout();
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleLogout();
                }}
              >
                {isLoggingOut ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saindo...
                  </>
                ) : (
                  <>
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('header.logout')}
                  </>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div> : <div className="flex items-center space-x-2">
            <Button variant="ghost" asChild>
              <Link to="/auth/login">{t('header.login')}</Link>
            </Button>
            <Button asChild>
              <Link to="/auth/signup">{t('header.signup')}</Link>
            </Button>
          </div>}
      </div>
    </header>;
}